import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  AlertTriangle, ArrowLeft, Check, Copy, CopyPlus, ExternalLink, Globe, GripVertical,
  HelpCircle, Image as ImageIcon, LayoutTemplate, ListOrdered, Lock, Megaphone, Monitor,
  MousePointerClick, Plus, Smartphone, Sparkles, Trash2, Trophy, Type, Hash,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { ProgramPageRenderer } from "@/components/programPage/ProgramPageRenderer";
import { createBlock, newId } from "@/components/programPage/templates";
import { contrastBetween } from "@/components/programPage/theme";
import {
  programPageService,
  type ProgramPageEditorBootstrap,
  type ProgramPageState,
} from "@/services/programPage.service";
import {
  PROGRAM_PAGE_LIMITS,
  type ProgramPageBlock,
  type ProgramPageBlockType,
  type ProgramPageDocument,
  type ProgramPageProgram,
  type ProgramPageStanding,
  type ProgramPageTheme,
} from "@/types/programPage";
import { BlockInspector } from "./BlockInspector";
import { ColorField, Field, IconField, TextField, ToggleField } from "./fields";

const BLOCK_META: Record<ProgramPageBlockType, { label: string; hint: string; icon: LucideIcon }> = {
  hero: { label: "Hero", hint: "Big headline, image and a button", icon: LayoutTemplate },
  tiers: { label: "Tiers", hint: "Your ladder, with live progress", icon: Trophy },
  features: { label: "Feature cards", hint: "Icon, title and text in a grid", icon: Sparkles },
  steps: { label: "Numbered list", hint: "How it works, or the rules", icon: ListOrdered },
  stats: { label: "Stats", hint: "A row of big numbers", icon: Hash },
  text: { label: "Text", hint: "A heading and paragraphs", icon: Type },
  image: { label: "Image", hint: "A full-width picture", icon: ImageIcon },
  faq: { label: "FAQ", hint: "Questions that fold open", icon: HelpCircle },
  cta: { label: "Call to action", hint: "A bold closing banner", icon: Megaphone },
};

function blockTitle(block: ProgramPageBlock): string {
  if ("heading" in block && block.heading) return block.heading;
  if ("title" in block && block.title) return block.title;
  if (block.type === "stats" && block.items.length) return block.items.map((i) => i.value).join(" · ");
  return BLOCK_META[block.type].label;
}

function errorMessage(error: unknown, fallback: string): string {
  const message = (error as AxiosError<{ message?: string | string[] }>)?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
}

/**
 * "View as" builds a pretend standing so the brand can see what each of its
 * affiliates will: halfway up whichever rung it picks.
 */
function simulateStanding(program: ProgramPageProgram | null, viewAs: string): Record<string, ProgramPageStanding> {
  if (!program || viewAs === "visitor") return {};
  const index = viewAs === "new" ? -1 : program.tiers.findIndex((t) => t.id === viewAs);
  const current = program.tiers[index] ?? null;
  const next = program.tiers[index + 1] ?? null;
  const floor = current?.threshold ?? 0;
  const totalSales = next ? floor + (next.threshold - floor) / 2 : floor * 1.25;
  return {
    [program.id]: {
      totalSales,
      totalOrders: Math.round(totalSales / 60),
      currentTierId: current?.id ?? null,
      nextTierId: next?.id ?? null,
      progress: next ? 50 : program.tiers.length ? 100 : 0,
      remainingToNext: next ? next.threshold - totalSales : 0,
    },
  };
}

interface Props {
  bootstrap: ProgramPageEditorBootstrap;
  initialDocument: ProgramPageDocument;
  onStartOver: () => void;
}

export function ProgramPageEditor({ bootstrap, initialDocument, onStartOver }: Props) {
  const queryClient = useQueryClient();
  const { programs } = bootstrap;
  const defaultProgram = programs.find((p) => p.isDefault) ?? programs[0] ?? null;

  const [doc, setDoc] = useState<ProgramPageDocument>(initialDocument);
  const [page, setPage] = useState<ProgramPageState | null>(bootstrap.page);
  // A draft picked from a template has never been saved, so it starts dirty.
  const [savedJson, setSavedJson] = useState(() => JSON.stringify(bootstrap.page?.draft ?? null));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<"blocks" | "design" | "settings">("blocks");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [viewAs, setViewAs] = useState("visitor");
  const previewRef = useRef<HTMLDivElement>(null);

  const dirty = JSON.stringify(doc) !== savedJson;
  const selected = doc.blocks.find((b) => b.id === selectedId) ?? null;
  const live = page?.status === "PUBLISHED";

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const docRef = useRef(doc);
  docRef.current = doc;

  /// The server normalizes (clamps, mints ids), so its copy becomes the truth —
  /// unless the brand kept typing while the request was out, in which case
  /// their newer edits win and the page simply stays dirty.
  const settle = (state: ProgramPageState, sent: ProgramPageDocument) => {
    setPage(state);
    if (state.draft && docRef.current === sent) {
      setDoc(state.draft);
      setSavedJson(JSON.stringify(state.draft));
    } else {
      setSavedJson(JSON.stringify(sent));
    }
    queryClient.invalidateQueries({ queryKey: ["program-page", "me"] });
  };

  const save = useMutation({
    mutationFn: (sent: ProgramPageDocument) => programPageService.saveDraft(sent),
    onSuccess: (state, sent) => {
      settle(state, sent);
      toast.success("Draft saved");
    },
    onError: (e) => toast.error(errorMessage(e, "Could not save the draft")),
  });
  const publish = useMutation({
    mutationFn: (sent: ProgramPageDocument) => programPageService.publish(sent),
    onSuccess: (state, sent) => {
      settle(state, sent);
      toast.success(bootstrap.portalLive ? "Your page is live" : "Published. It goes live once your portal is active.");
    },
    onError: (e) => toast.error(errorMessage(e, "Could not publish")),
  });
  const unpublish = useMutation({
    mutationFn: () => programPageService.unpublish(),
    onSuccess: (state) => {
      setPage(state);
      toast.success("Page taken down. Your draft is safe.");
    },
    onError: (e) => toast.error(errorMessage(e, "Could not unpublish")),
  });
  const settings = useMutation({
    mutationFn: programPageService.updateSettings,
    onSuccess: setPage,
    onError: (e) => toast.error(errorMessage(e, "Could not update settings")),
  });
  const busy = save.isPending || publish.isPending;

  const updateBlocks = (blocks: ProgramPageBlock[]) => setDoc((d) => ({ ...d, blocks }));
  const updateTheme = (changes: Partial<ProgramPageTheme>) =>
    setDoc((d) => ({ ...d, theme: { ...d.theme, ...changes } }));

  const select = (id: string | null, scroll = true) => {
    setSelectedId(id);
    if (id) setTab("blocks");
    if (id && scroll) {
      requestAnimationFrame(() =>
        previewRef.current
          ?.querySelector(`[data-block-id="${id}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" })
      );
    }
  };

  const addBlock = (type: ProgramPageBlockType) => {
    const rungColors = doc.blocks.flatMap((b) =>
      b.type === "tiers" ? b.items.map((i) => i.color).filter((c): c is string => !!c) : []
    );
    const block = createBlock(type, { program: defaultProgram, rungColors });
    const at = selectedId ? doc.blocks.findIndex((b) => b.id === selectedId) + 1 : doc.blocks.length;
    updateBlocks([...doc.blocks.slice(0, at), block, ...doc.blocks.slice(at)]);
    select(block.id);
  };

  const duplicate = (block: ProgramPageBlock) => {
    const copy = structuredClone(block);
    copy.id = newId();
    if ("items" in copy) copy.items.forEach((item) => (item.id = newId()));
    const at = doc.blocks.findIndex((b) => b.id === block.id) + 1;
    updateBlocks([...doc.blocks.slice(0, at), copy, ...doc.blocks.slice(at)]);
    select(copy.id);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = doc.blocks.findIndex((b) => b.id === active.id);
    const to = doc.blocks.findIndex((b) => b.id === over.id);
    updateBlocks(arrayMove(doc.blocks, from, to));
  };

  const standing = useMemo(() => simulateStanding(defaultProgram, viewAs), [defaultProgram, viewAs]);
  const customised = Object.values(doc.theme.colors).some(Boolean);
  // Only meaningful once both are overrides; inherited tokens are already legible.
  const textContrast =
    doc.theme.colors.text && doc.theme.colors.background
      ? contrastBetween(doc.theme.colors.text, doc.theme.colors.background)
      : null;
  const full = doc.blocks.length >= PROGRAM_PAGE_LIMITS.blocks;

  return (
    <div className="flex h-screen flex-col">
      {/* Top bar */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card py-3 pl-16 pr-5 md:pl-5">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Program page</h1>
          <StatusPill live={live} pending={live && (dirty || !!page?.hasUnpublishedChanges)} />
        </div>
        <div className="flex items-center gap-2">
          {live && bootstrap.pageUrl && (
            <Button variant="ghost" size="sm" asChild>
              <a href={bootstrap.pageUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-1.5 h-4 w-4" /> View live
              </a>
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={!dirty || busy} onClick={() => save.mutate(doc)}>
            {save.isPending ? "Saving…" : dirty ? "Save draft" : "Saved"}
          </Button>
          <Button
            size="sm"
            disabled={busy || !doc.blocks.length || (live && !dirty && !page?.hasUnpublishedChanges)}
            onClick={() => publish.mutate(doc)}
          >
            {publish.isPending ? "Publishing…" : live ? "Publish changes" : "Publish"}
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Controls */}
        <aside className="flex w-full shrink-0 flex-col border-b border-border bg-card lg:w-[380px] lg:border-b-0 lg:border-r">
          <UnderlineTabs
            className="px-4"
            value={tab}
            onValueChange={(next) => {
              setTab(next);
              if (next !== "blocks") setSelectedId(null);
            }}
            tabs={[
              { value: "blocks", label: "Content" },
              { value: "design", label: "Design" },
              { value: "settings", label: "Sharing" },
            ]}
          />
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {tab === "blocks" && selected && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setSelectedId(null)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" /> All blocks
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      title="Duplicate block"
                      aria-label="Duplicate block"
                      disabled={full}
                      onClick={() => duplicate(selected)}
                      className="rounded-md p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <CopyPlus className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Delete block"
                      aria-label="Delete block"
                      onClick={() => {
                        updateBlocks(doc.blocks.filter((b) => b.id !== selected.id));
                        setSelectedId(null);
                      }}
                      className="rounded-md p-1.5 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <h2 className="text-sm font-semibold">{BLOCK_META[selected.type].label}</h2>
                <BlockInspector
                  key={selected.id}
                  block={selected}
                  programs={programs}
                  onChange={(next) => updateBlocks(doc.blocks.map((b) => (b.id === next.id ? next : b)))}
                />
              </div>
            )}

            {tab === "blocks" && !selected && (
              <div className="flex flex-col gap-3">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext items={doc.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                    <ul className="flex flex-col gap-2">
                      {doc.blocks.map((block) => (
                        <BlockRow key={block.id} block={block} onOpen={() => select(block.id)} />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
                {!doc.blocks.length && (
                  <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                    An empty page. Add your first block below.
                  </p>
                )}
                <AddBlockMenu disabled={full} onAdd={addBlock} />
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MousePointerClick className="h-3.5 w-3.5" /> Click anything in the preview to edit it.
                </p>
              </div>
            )}

            {tab === "design" && (
              <div className="flex flex-col gap-5">
                <p className="text-sm text-muted-foreground">
                  The page uses your portal's theme — the same font, colours and light/dark mode as the rest of the
                  app — so it feels like one product. Override a colour only if you need to.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      ["background", "Background"],
                      ["surface", "Cards"],
                      ["text", "Text"],
                      ["accent", "Accent"],
                    ] as const
                  ).map(([key, label]) => (
                    <ColorField
                      key={key}
                      label={label}
                      value={doc.theme.colors[key]}
                      onChange={(value) => updateTheme({ colors: { ...doc.theme.colors, [key]: value } })}
                      onClear={() => updateTheme({ colors: { ...doc.theme.colors, [key]: null } })}
                      placeholder="App theme"
                    />
                  ))}
                </div>
                {customised && (
                  <button
                    type="button"
                    onClick={() => updateTheme({ colors: { background: null, surface: null, text: null, accent: null } })}
                    className="self-start text-xs font-medium text-brand-700 hover:underline dark:text-brand-300"
                  >
                    Back to the app theme
                  </button>
                )}
                {textContrast !== null && textContrast < 4.5 && (
                  <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                    Your text is hard to read on this background ({textContrast.toFixed(1)}:1). Aim for 4.5:1 or higher.
                  </p>
                )}
                <ToggleField
                  label="Number the sections"
                  hint="01, 02, 03 above each heading."
                  checked={doc.theme.sectionNumbers}
                  onChange={(sectionNumbers) => updateTheme({ sectionNumbers })}
                />
                <button
                  type="button"
                  onClick={onStartOver}
                  className="self-start text-xs text-muted-foreground underline hover:text-foreground"
                >
                  Start over from a template
                </button>
              </div>
            )}

            {tab === "settings" && (
              <div className="flex flex-col gap-5">
                <Field label="Who can see this page">
                  <div className="flex flex-col gap-2">
                    {(
                      [
                        ["AFFILIATES", Lock, "Your affiliates only", "Shown in their sidebar once they've joined you."],
                        ["PUBLIC", Globe, "Anyone with the link", "No signup needed. Use it to recruit."],
                      ] as const
                    ).map(([value, Icon, title, hint]) => {
                      const active = (page?.visibility ?? "AFFILIATES") === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          disabled={settings.isPending}
                          onClick={() => !active && settings.mutate({ visibility: value })}
                          className={`flex items-start gap-3 rounded-lg border p-3 text-left ${
                            active ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10" : "border-border hover:border-brand-400"
                          }`}
                        >
                          <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                          <span className="flex flex-col">
                            <span className="text-sm font-medium">{title}</span>
                            <span className="text-xs text-muted-foreground">{hint}</span>
                          </span>
                          {active && <Check className="ml-auto h-4 w-4 shrink-0 text-brand-600" />}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {bootstrap.pageUrl ? (
                  <Field label="Page link" hint={live ? undefined : "Works once you publish."}>
                    <div className="flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted px-3 py-2 text-xs">
                        {bootstrap.pageUrl}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigator.clipboard
                            .writeText(bootstrap.pageUrl!)
                            .then(() => toast.success("Link copied"))
                            .catch(() => toast.error("Could not copy the link"))
                        }
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </Field>
                ) : (
                  <p className="rounded-lg border border-border bg-muted p-3 text-xs text-muted-foreground">
                    Your portal has no address yet, so this page has nowhere to live. Choose one under Settings → Customizations, then publish.
                  </p>
                )}

                <NavSettings
                  key={`${page?.navLabel}-${page?.navIcon}`}
                  label={page?.navLabel ?? "Rewards program"}
                  icon={page?.navIcon ?? "Trophy"}
                  onSave={(changes) => settings.mutate(changes)}
                />

                {live && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={unpublish.isPending}
                    onClick={() => unpublish.mutate()}
                  >
                    Take the page down
                  </Button>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Preview */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-muted">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 py-2">
            <div className="flex rounded-lg border border-border p-0.5">
              {(
                [
                  ["desktop", Monitor],
                  ["mobile", Smartphone],
                ] as const
              ).map(([value, Icon]) => (
                <button
                  key={value}
                  type="button"
                  aria-label={`${value} preview`}
                  aria-pressed={device === value}
                  onClick={() => setDevice(value)}
                  className={`rounded-md p-1.5 ${
                    device === value ? "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300" : "text-muted-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              View as
              <select
                value={viewAs}
                onChange={(e) => setViewAs(e.target.value)}
                className="h-8 rounded-lg border border-input bg-transparent px-2 text-xs text-foreground"
              >
                <option value="visitor">A visitor</option>
                <option value="new">A brand-new affiliate</option>
                {defaultProgram?.tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    An affiliate at {tier.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div ref={previewRef} className="min-h-0 flex-1 overflow-y-auto p-4">
            <div
              className="mx-auto overflow-hidden rounded-xl border border-border shadow-sm transition-[max-width] duration-300"
              style={{ maxWidth: device === "mobile" ? 390 : 1100 }}
            >
              <ProgramPageRenderer
                document={doc}
                programs={programs}
                defaultProgramId={defaultProgram?.id ?? null}
                standing={standing}
                joinUrl={viewAs === "visitor" ? "#" : null}
                selectedBlockId={selectedId}
                onSelectBlock={(id) => select(id, false)}
                className="min-h-[60vh]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ live, pending }: { live: boolean; pending: boolean }) {
  const [label, tone] = !live
    ? ["Draft", "bg-muted text-muted-foreground"]
    : pending
      ? ["Live · unpublished changes", "bg-amber-500/15 text-amber-700 dark:text-amber-300"]
      : ["Live", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
}

function BlockRow({ block, onOpen }: { block: ProgramPageBlock; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const meta = BLOCK_META[block.type];
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
        transition,
      }}
      className={`flex items-center gap-1 rounded-lg border border-border bg-card ${isDragging ? "z-10 shadow-lg" : ""}`}
    >
      <button
        type="button"
        aria-label={`Reorder ${meta.label}`}
        className="cursor-grab touch-none p-2.5 text-muted-foreground hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pr-3 text-left">
        <meta.icon className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{blockTitle(block)}</span>
          <span className="text-xs text-muted-foreground">{meta.label}</span>
        </span>
      </button>
    </li>
  );
}

function AddBlockMenu({ disabled, onAdd }: { disabled: boolean; onAdd: (type: ProgramPageBlockType) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="w-full">
          <Plus className="mr-1.5 h-4 w-4" />
          {disabled ? `Limit of ${PROGRAM_PAGE_LIMITS.blocks} blocks reached` : "Add a block"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-2">
        <div className="grid grid-cols-1 gap-0.5">
          {(Object.keys(BLOCK_META) as ProgramPageBlockType[]).map((type) => {
            const meta = BLOCK_META[type];
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  onAdd(type);
                  setOpen(false);
                }}
                className="flex items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-accent"
              >
                <meta.icon className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                <span className="flex flex-col">
                  <span className="text-sm font-medium">{meta.label}</span>
                  <span className="text-xs text-muted-foreground">{meta.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The affiliate sidebar entry. Saved on blur/pick rather than per keystroke. */
function NavSettings({
  label,
  icon,
  onSave,
}: {
  label: string;
  icon: string;
  onSave: (changes: { navLabel?: string; navIcon?: string }) => void;
}) {
  const [text, setText] = useState(label);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <p className="text-sm font-medium">In your affiliates' sidebar</p>
      <div
        onBlur={() => {
          const next = text.trim();
          if (next && next !== label) onSave({ navLabel: next });
        }}
      >
        <TextField label="Label" value={text} maxLength={28} onChange={setText} />
      </div>
      <IconField
        label="Icon"
        lucideOnly
        value={{ kind: "lucide", value: icon }}
        onChange={(next) => next?.kind === "lucide" && next.value !== icon && onSave({ navIcon: next.value })}
      />
    </div>
  );
}
